'use client';

import { Input } from '@app/app/_components/Input/Input';
import { Select } from '@app/app/_components/Input/Select/Select';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { SearchIcon } from '@app/app/_icons/SearchIcon';
import { Address } from '@app/domain/model/Address';
import { AddressAutocompleteResult } from '@app/infrastructure/address/model/AddressAutocompleteResult';
import { useAddressAutocomplete } from '@app/infrastructure/address/useAddressAutocomplete';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';
import { GhostButton } from '../GhostButton/GhostButton';
import { generateId } from '@app/lib/id-generator/generateId';

interface Props {
    countryOptions: SelectOption[];
    namePrefix?: string;
    defaultValue?: Address;
    violations?: ViolationMap;
    onChange?: (address: Nullable<Address>) => void;
    disableCountrySelect?: boolean;
    defaultCountry?: string;
    allowManualEntry?: boolean;
}

export function AddressInput({
    countryOptions,
    namePrefix,
    defaultValue,
    violations,
    onChange,
    disableCountrySelect,
    defaultCountry,
    allowManualEntry = true,
}: Props) {
    const t = useTranslations();

    const { search, results } = useAddressAutocomplete();

    const initialSelectedAddress = useMemo(() => {
        if (!allowManualEntry && defaultValue) {
            return {
                id: generateId(),
                title: '',
                language: '',
                address: {
                    label: `${defaultValue.street || ''} ${defaultValue.houseNumber || ''}`.trim(),
                    countryCode: defaultValue.country || '',
                    countryName: '',
                    stateCode: '',
                    state: '',
                    countyCode: '',
                    county: defaultValue.municipality || '',
                    city: defaultValue.city || '',
                    district: defaultValue.municipality || '',
                    street: defaultValue.street || '',
                    houseNumber: defaultValue.houseNumber || undefined,
                    postalCode: defaultValue.postalCode || '',
                },
            };
        }
        return null;
    }, [defaultValue, allowManualEntry]);

    const countryFromAddress = useMemo(() => {
        const country = defaultValue?.country;

        return country && countryOptions.some(item => item.value === country) ? country : null;
    }, [defaultValue?.country, countryOptions]);
    const [manual, setManual] = useState(allowManualEntry && defaultValue != null);
    const [selectedAddress, setSelectedAddress] = useState<Nullable<AddressAutocompleteResult>>(initialSelectedAddress);
    const [selectedCountry, setSelectedCountry] = useState<Nullable<string>>(
        countryFromAddress || defaultCountry || null,
    );

    const [formValues, setFormValues] = useState({
        street: defaultValue?.street || '',
        houseNumber: defaultValue?.houseNumber || '',
        apartmentNumber: defaultValue?.apartmentNumber || '',
        city: defaultValue?.city || '',
        postalCode: defaultValue?.postalCode || '',
        municipality: defaultValue?.municipality || '',
    });

    const addressError = useMemo(() => {
        const cityField = namePrefix != null ? `${namePrefix}City` : 'city';
        const streetField = namePrefix != null ? `${namePrefix}Street` : 'street';
        const postalCodeField = namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode';
        const houseNumberField = namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber';

        return violations?.[cityField] != null ||
            violations?.[streetField] != null ||
            violations?.[postalCodeField] != null ||
            violations?.[houseNumberField] != null
            ? { message: 'address.validation.addressNotPreciseEnough' }
            : undefined;
    }, [violations, namePrefix]);

    const notifyAddressChange = useCallback(() => {
        if (!onChange) return;

        if (!selectedCountry) {
            onChange(null);
            return;
        }

        if (manual) {
            const address: Address = {
                govId: null,
                id: null,
                country: selectedCountry,
                street: formValues.street,
                houseNumber: formValues.houseNumber,
                apartmentNumber: formValues.apartmentNumber,
                city: formValues.city,
                postalCode: formValues.postalCode,
                municipality: formValues.municipality,
                streetAddress: null,
                county: null,
                main: true,
            };
            onChange(address);
        } else if (selectedAddress) {
            const address: Address = {
                govId: null,
                id: null,
                country: selectedCountry,
                street: selectedAddress.address.street || '',
                houseNumber: selectedAddress.address.houseNumber || '',
                apartmentNumber: formValues.apartmentNumber,
                city: selectedAddress.address.city || '',
                postalCode: selectedAddress.address.postalCode || '',
                municipality: selectedAddress.address.county || '',
                streetAddress: null,
                county: null,
                main: true,
            };
            onChange(address);
        } else {
            onChange(null);
        }
    }, [onChange, selectedCountry, manual, formValues, selectedAddress]);

    useEffect(() => {
        notifyAddressChange();
    }, [notifyAddressChange]);

    const handleEnterManuallyClick = useCallback(() => {
        setManual(true);
    }, []);

    const handleUseAddressSearchClick = useCallback(() => {
        setManual(false);
        setSelectedAddress(null);
    }, []);

    const handleAddressSelected = useCallback(
        (addressId: string) => {
            const selected = results.find(r => r.id === addressId) ?? null;
            setSelectedAddress(selected);

            if (selected) {
                setFormValues(prev => ({
                    ...prev,
                    street: selected.address.street || '',
                    houseNumber: selected.address.houseNumber || '',
                    city: selected.address.city || '',
                    postalCode: selected.address.postalCode || '',
                    municipality: selected.address.county || '',
                }));
            }
        },
        [results],
    );

    const handleCountrySelected = useCallback((country: string) => {
        setSelectedCountry(country);
    }, []);

    const handleSearch = useDebouncedCallback((searchQuery: string) => {
        void search(searchQuery, selectedCountry ?? undefined);
    }, 400);

    const handleInputChange = useCallback((field: string, value: string) => {
        setFormValues(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleStreetChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('street', e.target.value);
        },
        [handleInputChange],
    );

    const handleHouseNumberChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('houseNumber', e.target.value);
        },
        [handleInputChange],
    );

    const handleApartmentNumberChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('apartmentNumber', e.target.value);
        },
        [handleInputChange],
    );

    const handleCityChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('city', e.target.value);
        },
        [handleInputChange],
    );

    const handlePostalCodeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('postalCode', e.target.value);
        },
        [handleInputChange],
    );

    const handleMunicipalityChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('municipality', e.target.value);
        },
        [handleInputChange],
    );

    const addressSearchOptions = useMemo(
        () =>
            results.map(result => ({
                value: result.id,
                label: result.address.label,
            })),
        [results],
    );

    return (
        <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-4">
                {!disableCountrySelect && (
                    <div className="md:col-span-4">
                        <Select
                            name={namePrefix != null ? `${namePrefix}Country` : 'country'}
                            label={t('address.countryLabel')}
                            placeholder={t('address.countryPlaceholder')}
                            defaultValue={selectedCountry ?? undefined}
                            error={violations?.[namePrefix != null ? `${namePrefix}Country` : 'country']}
                            options={countryOptions}
                            onChange={handleCountrySelected}
                            noResultsHint={t('address.noCountryResultsHint')}
                            searchable
                        />
                    </div>
                )}

                {disableCountrySelect && (
                    <input
                        type="hidden"
                        name={namePrefix != null ? `${namePrefix}Country` : 'country'}
                        value={selectedCountry ?? ''}
                    />
                )}

                {!manual && (
                    <div className="min-w-0 md:col-span-3">
                        <Select
                            name="addressSearch"
                            autoFocus
                            label={t('address.addressSearchLabel')}
                            placeholder={t('address.addressSearchPlaceholder')}
                            error={addressError}
                            options={addressSearchOptions}
                            defaultValue={selectedAddress?.id}
                            onChange={handleAddressSelected}
                            searchable
                            onSearch={handleSearch}
                            searchHint={t('address.typeToSearchHint')}
                            noResultsHint={t('address.noAddressResultsHint')}
                            bottomAddon={
                                allowManualEntry ? (
                                    <button
                                        type="button"
                                        className={twJoin(
                                            'inline-flex cursor-pointer py-2.5 md:px-3 md:py-1.25',
                                            'rounded text-sm font-medium text-primary-500 hover:bg-neutral-100',
                                        )}
                                        onClick={handleEnterManuallyClick}>
                                        {t('address.enterAddressManually')}
                                    </button>
                                ) : undefined
                            }
                            rightIcon={<SearchIcon className="size-4" />}
                            disabled={selectedCountry == null}
                        />
                    </div>
                )}

                {manual && allowManualEntry && (
                    <>
                        <div className="md:col-span-4">
                            <GhostButton
                                type="button"
                                className={twJoin(
                                    'inline-flex cursor-pointer items-center gap-2 px-3 py-2',
                                    'hover:bg-primary-50 rounded border border-primary-200 text-sm font-medium text-primary-500',
                                )}
                                onClick={handleUseAddressSearchClick}
                                disabled={selectedCountry == null}>
                                <SearchIcon className="size-4" />
                                {t('profile.useAddressSearch')}
                            </GhostButton>
                        </div>

                        <div className="md:col-span-2">
                            <Input
                                name={namePrefix != null ? `${namePrefix}Street` : 'street'}
                                label={t('address.streetLabel')}
                                placeholder={t('address.streetPlaceholder')}
                                value={formValues.street}
                                onChange={handleStreetChange}
                                error={violations?.[namePrefix != null ? `${namePrefix}Street` : 'street']}
                            />
                        </div>
                        <div className="md:col-span-1">
                            <Input
                                name={namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber'}
                                label={t('address.houseNumberLabel')}
                                placeholder={t('address.houseNumberPlaceholder')}
                                value={formValues.houseNumber}
                                onChange={handleHouseNumberChange}
                                error={violations?.[namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber']}
                            />
                        </div>
                    </>
                )}

                <div className="md:col-span-1">
                    <Input
                        name={namePrefix != null ? `${namePrefix}ApartmentNumber` : 'apartmentNumber'}
                        label={t('address.apartmentNumberLabel')}
                        placeholder={t('address.apartmentNumberPlaceholder')}
                        value={formValues.apartmentNumber}
                        onChange={handleApartmentNumberChange}
                        error={violations?.[namePrefix != null ? `${namePrefix}ApartmentNumber` : 'apartmentNumber']}
                    />
                </div>

                {manual && allowManualEntry && (
                    <>
                        <div className="md:col-span-3">
                            <Input
                                name={namePrefix != null ? `${namePrefix}City` : 'city'}
                                label={t('address.cityLabel')}
                                placeholder={t('address.cityPlaceholder')}
                                value={formValues.city}
                                onChange={handleCityChange}
                                error={violations?.[namePrefix != null ? `${namePrefix}City` : 'city']}
                            />
                        </div>
                        <div className="md:col-span-1">
                            <Input
                                name={namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode'}
                                label={t('address.postalCodeLabel')}
                                placeholder={t('address.postalCodePlaceholder')}
                                value={formValues.postalCode}
                                onChange={handlePostalCodeChange}
                                error={violations?.[namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode']}
                            />
                        </div>
                        <div className="md:col-span-4">
                            <Input
                                name={namePrefix != null ? `${namePrefix}Municipality` : 'municipality'}
                                label={t('address.municipalityLabel')}
                                placeholder={t('address.municipalityPlaceholder')}
                                value={formValues.municipality}
                                onChange={handleMunicipalityChange}
                                error={violations?.[namePrefix != null ? `${namePrefix}Municipality` : 'municipality']}
                            />
                        </div>
                    </>
                )}

                {!manual && (
                    <>
                        <input
                            type="hidden"
                            name={namePrefix != null ? `${namePrefix}Street` : 'street'}
                            value={selectedAddress?.address.street ?? ''}
                        />
                        <input
                            type="hidden"
                            name={namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber'}
                            value={selectedAddress?.address.houseNumber ?? ''}
                        />
                        <input
                            type="hidden"
                            name={namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode'}
                            value={selectedAddress?.address.postalCode ?? ''}
                        />
                        <input
                            type="hidden"
                            name={namePrefix != null ? `${namePrefix}City` : 'city'}
                            value={selectedAddress?.address.city ?? ''}
                        />
                        <input
                            type="hidden"
                            name={namePrefix != null ? `${namePrefix}Municipality` : 'municipality'}
                            value={selectedAddress?.address.county ?? ''}
                        />

                        <div className="md:col-span-2">
                            <ReadOnlyField label={t('address.streetAndHouseNumberLabel')}>
                                {selectedAddress?.address.street ?? '-'}&nbsp;
                                {selectedAddress?.address.houseNumber ?? ''}
                            </ReadOnlyField>
                        </div>
                        <div className="md:col-span-2">
                            <ReadOnlyField label={t('address.postalCodeLabel')}>
                                {selectedAddress?.address.postalCode ?? '-'}
                            </ReadOnlyField>
                        </div>
                        <div className="md:col-span-2">
                            <ReadOnlyField label={t('address.cityLabel')}>
                                {selectedAddress?.address.city ?? '-'}
                            </ReadOnlyField>
                        </div>
                        <div className="md:col-span-2">
                            <ReadOnlyField label={t('address.municipalityLabel')}>
                                {selectedAddress?.address.county ?? '-'}
                            </ReadOnlyField>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
